import { useCallback, useContext, useMemo } from 'react'
import type { ComponentProps } from 'react'
import { useIntl } from 'react-intl'

import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Clickable from 'components/Common/Clickable'
import ModalContext from 'contexts/ModalContext'

import AccommodationRoomDetailsListContent from './AccommodationRoomDetailsListContent'

interface Props extends ComponentProps<typeof AccommodationRoomDetailsListContent> {
  shouldShowAmenitiesAndPoliciesCta?: boolean
}

function AccommodationRoomDetailsList(props: Props) {
  const {
    roomPackage,
    urgencyLabels,
    hideCounts,
    shouldShowAmenitiesAndPoliciesCta = true,
    showHotelRoomAmenities = false,
    showAmenitiesAndPoliciesCtaOnNewLine = false,
  } = props

  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const openAmenitiesAndPoliciesModal = useCallback(async() => {
    const { default: AccommodationAmenitiesAndPoliciesModal } = await import('./AccommodationAmenitiesAndPoliciesModal')
    showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={roomPackage} hideCounts={hideCounts} />)
  }, [roomPackage, showModal, hideCounts])

  const viewAmenitiesAndPoliciesText = intl.formatMessage({
    id: 'RqhmmXe05aLenwFTiitr',
    defaultMessage: 'View amenities & policies',
    description: 'CTA text that opens the room amenities & policies modal.',
  })

  const amenitiesAndPoliciesCta = useMemo(() => {
    if (!shouldShowAmenitiesAndPoliciesCta) return undefined

    return <Clickable onClick={openAmenitiesAndPoliciesModal}>
      <BodyTextBlock variant="medium" endIcon={<LineAngleRightIcon aria-hidden="true" />} underline>{viewAmenitiesAndPoliciesText}</BodyTextBlock>
    </Clickable>
  }, [openAmenitiesAndPoliciesModal, shouldShowAmenitiesAndPoliciesCta, viewAmenitiesAndPoliciesText])

  return <AccommodationRoomDetailsListContent
    roomPackage={roomPackage}
    urgencyLabels={urgencyLabels}
    hideCounts={hideCounts}
    showHotelRoomAmenities={showHotelRoomAmenities}
    showAmenitiesAndPoliciesCtaOnNewLine={showAmenitiesAndPoliciesCtaOnNewLine}
    amenitiesAndPoliciesCta={amenitiesAndPoliciesCta}
  />
}

export default AccommodationRoomDetailsList
